import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import { useCompactScreen } from './session-state';

/** Window desktop lists; keep the focused row and its neighbours mounted for Tab. */
export function VirtualRows<T>({ items, itemKey, children, estimate = 64, enabled = true, pinnedKey }: {
  items: T[]; itemKey(item: T): string; children(item: T, index: number): ReactNode; estimate?: number; enabled?: boolean; pinnedKey?: string | undefined;
}) {
  const compact = useCompactScreen();
  const root = useRef<HTMLDivElement>(null);
  const [owner, setOwner] = useState<HTMLElement | null>(null);
  const [margin, setMargin] = useState(0);
  const [focused, setFocused] = useState<number>();
  const windowed = enabled && items.length > 40;
  useLayoutEffect(() => {
    let parent = root.current?.parentElement;
    while (parent && !/auto|scroll/.test(getComputedStyle(parent).overflowY)) parent = parent.parentElement;
    setOwner(parent ?? null);
    const measure = () => { if (root.current && parent) setMargin(root.current.getBoundingClientRect().top - parent.getBoundingClientRect().top + parent.scrollTop); };
    measure();
    const observer = new ResizeObserver(measure);
    if (parent) observer.observe(parent);
    return () => observer.disconnect();
  }, [windowed, compact]);
  const virtual = useVirtualizer({ count: items.length, getScrollElement: () => owner, estimateSize: () => estimate,
    getItemKey: index => itemKey(items[index]!), overscan: 6, scrollMargin: margin, enabled: windowed,
    rangeExtractor: range => {
      const indices = defaultRangeExtractor(range);
      const pinned = pinnedKey ? items.findIndex(item=>itemKey(item)===pinnedKey) : -1;
      if (pinned >= 0) indices.push(pinned);
      if (focused !== undefined) for (let i = Math.max(0, focused - 1); i <= Math.min(items.length - 1, focused + 1); i++) indices.push(i);
      return [...new Set(indices)].sort((a, b) => a - b);
    },
  });
  const rows = virtual.getVirtualItems();
  let previousEnd = margin;
  return <div ref={root} className="virtual-rows" onFocusCapture={event => {
    const row = (event.target as HTMLElement).closest<HTMLElement>('[data-virtual-index]');
    if (row) setFocused(Number(row.dataset.virtualIndex));
  }} onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setFocused(undefined); }}>
    {!windowed ? items.map(children) : !owner ? <>
      {items.slice(0, Math.ceil(window.innerHeight / estimate) + 6).map(children)}
      <div aria-hidden style={{ height: Math.max(0, items.length - Math.ceil(window.innerHeight / estimate) - 6) * estimate }} />
    </> : <>
      {rows.map(row => { const gap = Math.max(0, row.start - previousEnd); previousEnd = row.end; return <div key={row.key}>
        {gap > 0 && <div aria-hidden style={{ height: gap }} />}
        <div data-index={row.index} data-virtual-index={row.index} ref={virtual.measureElement}>{children(items[row.index]!, row.index)}</div>
      </div>; })}
      <div aria-hidden style={{ height: Math.max(0, virtual.getTotalSize() + margin - previousEnd) }} />
    </>}
  </div>;
}
